/**
 * Back in stock subscription for the quick view.
 *
 * Klaviyo's native modal (klaviyo.enable("backinstock")) only initializes on product
 * templates. The quick view renders on collection, search and page templates, where
 * Shopify exposes no `meta.product` and Klaviyo answers "Unable to process event:
 * enable,backinstock". So the quick view posts to Klaviyo's client API directly.
 *
 * Listeners are delegated from `document` because Simplistic.openModal injects the
 * quick view with innerHTML, which never executes injected <script> tags.
 *
 * Markup: snippets/klaviyo_bis_form.liquid
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var REVISION = '2024-06-15';
  var VARIANT_PREFIX = '$shopify:::$default:::';

  function getRoot(element) {
    return element.closest('[data-bis]');
  }

  /**
   * The variant the shopper currently has selected. ProductJs keeps the hidden
   * [name="id"] input in sync, so it is the source of truth. The rendered default
   * covers the window before ProductJs initializes.
   */
  function getVariantId(root) {
    var form = root.closest('form.add-to-cart-form');
    var input = form && form.querySelector('[name="id"]');
    return (input && input.value) || root.dataset.defaultVariant || '';
  }

  /**
   * @param {'success'|'error'|''} tone - drives the coloured pill, matching Klaviyo's own modal
   */
  function setStatus(root, message, tone) {
    var status = root.querySelector('[data-bis-status]');
    status.textContent = message;
    status.classList.toggle('bis-status-error', tone === 'error');
    status.classList.toggle('bis-status-success', tone === 'success');
  }

  // The trigger gives way to the form rather than sitting above it, so the shopper never
  // sees two identical "Notify me when available" buttons stacked on top of each other.
  function revealForm(root) {
    root.querySelector('[data-bis-trigger]').hidden = true;
    root.querySelector('[data-bis-panel]').hidden = false;
    root.querySelector('[data-bis-email]').focus();
  }

  async function subscribe(root) {
    var input = root.querySelector('[data-bis-email]');
    var submit = root.querySelector('[data-bis-submit]');

    if (!input.value.trim() || !input.checkValidity()) {
      setStatus(root, root.dataset.msgErrorEmail, 'error');
      input.focus();
      return;
    }

    var variantId = getVariantId(root);
    if (!variantId) {
      setStatus(root, root.dataset.msgErrorGeneric, 'error');
      return;
    }

    submit.disabled = true;
    setStatus(root, root.dataset.msgSubmitting, '');

    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: input.value.trim() } } },
          channels: ['EMAIL']
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: VARIANT_PREFIX + variantId } }
        }
      }
    };

    try {
      var response = await fetch(ENDPOINT + '?company_id=' + encodeURIComponent(root.dataset.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify(payload)
      });

      // Klaviyo answers 202 with an empty body on success.
      if (response.status === 202) {
        setStatus(root, root.dataset.msgSuccess, 'success');
        input.disabled = true;
        submit.hidden = true;
        return;
      }

      var body = await response.json().catch(function () { return null; });
      var pointer = body && body.errors && body.errors[0] && body.errors[0].source && body.errors[0].source.pointer;
      var isEmailError = typeof pointer === 'string' && pointer.indexOf('email') !== -1;

      setStatus(root, isEmailError ? root.dataset.msgErrorEmail : root.dataset.msgErrorGeneric, 'error');
      submit.disabled = false;
    } catch (error) {
      setStatus(root, root.dataset.msgErrorGeneric, 'error');
      submit.disabled = false;
    }
  }

  /**
   * A status message only ever describes the variant it was submitted for, so selecting
   * a different one clears it. The success state also disables the field and hides the
   * submit, which would otherwise leave the shopper unable to subscribe to the new
   * variant. The typed email survives: it is the one thing they would hate to retype.
   *
   * @param {HTMLElement} root - the [data-bis] container
   */
  function resetPanel(root) {
    var input = root.querySelector('[data-bis-email]');
    var submit = root.querySelector('[data-bis-submit]');

    setStatus(root, '', '');
    input.disabled = false;
    submit.hidden = false;
    submit.disabled = false;
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-bis-trigger]');
    if (trigger) {
      revealForm(getRoot(trigger));
      return;
    }

    var submit = event.target.closest('[data-bis-submit]');
    if (submit) subscribe(getRoot(submit));
  });

  // Enter inside the email field would otherwise submit the surrounding add-to-cart form.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' || !event.target.closest('[data-bis-email]')) return;
    event.preventDefault();
    subscribe(getRoot(event.target));
  });

  // ProductJs fires variantChange through jQuery, so it is not a real DOM event.
  // Detach/restore of [data-bis] lives in ProductJs.updateButtons; here we only
  // clear a previous subscription status when the shopper picks another sold-out variant.
  if (window.jQuery) {
    window.jQuery(document).on('variantChange', function (event, variant) {
      if (!variant || variant.available) return;

      var productRoot = event.target;
      var root = productRoot && productRoot.querySelector && productRoot.querySelector('[data-bis]');
      if (root) resetPanel(root);
    });
  }
})();
